import React, { useMemo, useContext, useState, useEffect, useCallback, useRef } from "react";
import { Grid, Form, List, Button, Icon, Popup, Label } from "semantic-ui-react";
import { PriceListContext } from './context';
import { useMessageResources, useTextResources } from "../../language";
import RoomTypesDropDown from "../../components/shared/RoomTypesDropDown";
import PackagesDropDown from "../../components/shared/PackagesDropDown";
import { ChainAddonsDropDown } from "../../components/shared/ChainAddonsDropDown";
import { RoomTypes, Packages, Addons, PriceLists, Rates, PricingTemplates } from "../../api/services/next";
import Panel from "../../components/shared/Panel";
import { useCurrentHotel } from "../../hooks/useCurrentHotel";
import EntityTypes from "../GroupReservation/Constants/EntityTypes";
import GridPanel from "../../components/GridPanel";
import { RateTypes, dateFormat, addonOptionsFields, RateCategory, RatePricingTypesEnum, DerivedCategories, AllWeekDays, EntityType } from "../../components/shared/constants";
import { PricingPerPersonPopup } from '../PriceList/Pricing/PricingPerPersonPopup'
import { PricingPerRoomPopup } from '../PriceList/Pricing/PricingPerRoomPopup'
import { PricingPerVillaPopup } from '../PriceList/Pricing/PricingPerVillaPopup'
import { AddonOptionsPopup } from '../PriceList/Pricing/AddonOptionsPopup'
import { getWeekDaysOfRange, groupByIntoArray, makeDaysOfWeekArrayIntoInt, cloneEntityWithoutSpecificFields, isCovered } from '../../components/shared/utils'
import { onError, onSuccess, onWarning } from '../../components/shared/messages';
import moment from 'moment';
import DropDown from "../../components/shared/dropdown";
import { PerPersonAddonPopup } from "./Pricing/PerPersonAddonPopup";
import { CombinationsPopup } from "./Pricing/CombinationsPopup";
import { useConfirm } from "../../hooks/useConfirm";
import { SupplementPopup } from "./Pricing/SupplementPopup";
import { HideControlsByRateType } from "./HideControlsByRateType";
import { useLocalStorageState } from "../../hooks/useLocalStorageState";
import { PerRoomAddonPopup } from "./Pricing/PerRoomAddonPopup";
import { setRPEsWithRearangedExceptions, RPEsEditRearangeDateranges } from "./Pricing/pricingUtils";
import { useLoader } from "../../hooks/useLoader";

const ALL_OPTION_ID = 0;

export const Pricing = () => {
    const text = useTextResources();
    const message = useMessageResources();
    const confirm = useConfirm();
    const currentHotel = useCurrentHotel();
    const { state, setState, setDirtyState } = useContext(PriceListContext);
    const [gridData, setGridData] = useState([])
    const [roomTypes, setRoomTypes] = useState([]);
    const [selectedRoomTypes, setSelectedRoomTypes] = useState([]);
    const [packages, setPackages] = useState([]);
    const [selectedPackages, setSelectedPackages] = useState([]);
    const [addons, setAddons] = useState([]);
    const [selectedAddons, setSelectedAddons] = useState([]);
    const [pricingTemplates, setPricingTemplates] = useState(null);

    const [loadedFlag, setLoadedFlag] = useState(false);
    const [selectedEntity, setSelectedEntity] = useState({});
    const [showPricingPerPersonPopup, setShowPricingPerPersonPopup] = useState(false);
    const [showPricingPerRoomPopup, setShowPricingPerRoomPopup] = useState(false);
    const [showPricingPerPVillaPopup, setShowPricingPerVillaPopup] = useState(false);
    const [showAddonOptionsPopup, setShowAddonOptionsPopup] = useState(false);
    const [showPersonAddonPopup, setShowPersonAddonPopup] = useState(false);
    const [showRoomAddonPopup, setShowRoomAddonPopup] = useState(false);
    const [showCombinationsPopup, setShowCombinationsPopup] = useState(false);
    const [showSupplementPopup, setShowSupplementPopup] = useState(false);
    const [collapsed, setCollapsed] = useLocalStorageState('CollapsePricingPanels', false)

    const loader = useLoader();

    const allOption = useMemo(() => ({ Id: ALL_OPTION_ID, Name: text("All") }), [text]);
    const isAllRoomTypesSelected = selectedRoomTypes.some(rt => rt === ALL_OPTION_ID);
    const isAllPackagesSelected = selectedPackages.some(p => p === ALL_OPTION_ID);
    const isAllAddonsSelected = selectedAddons.some(a => a === ALL_OPTION_ID);

    const RateTypeDropDownData =
        [
            { Id: 1, Name: text(RateTypes[1]) },
            { Id: 2, Name: text(RateTypes[2]) },
            { Id: 3, Name: text(RateTypes[3]) }
        ]

    const isDerived = useMemo(() => {
        return DerivedCategories.includes(state.RateCategoryId)
    }, [state?.RateCategoryId]);

    const dataLoadedRef = useRef(false);
    useEffect(() => {
        if (dataLoadedRef.current) return;
        dataLoadedRef.current = true;
        (async () => {
            try {
                loader(true);

                const [roomTypesData, packagesData, addonsData, pricingTemplates] = await Promise.all([
                    RoomTypes.GetRoomTypes({ HotelId: state.HotelId }),
                    Packages.GetPackages({ HotelChainId: state.HotelChainId, IncludePricingElements: true, IsActive: true }),
                    Addons.GetHotelChainAddons({
                        HotelId: state.HotelId, IsActive: true,
                        FromDate: moment(state.StartDate).startOf("day").toISOString(true),
                        ToDate: moment(state.EndDate).startOf("day").toISOString(true),
                        includes: ["RoomTypeAddons"]
                    }),
                    PricingTemplates.GetPricingTemplates()
                ]);

                if (state.RateCategoryId !== RateCategory.ExtraAddOn) {
                    setAddons(addonsData.filter(a => a.RoomTypeAddons.length == 0))
                }
                else {
                    setAddons(addonsData);
                }
                setRoomTypes(roomTypesData);
                setPackages(packagesData);

                setPricingTemplates(pricingTemplates);
                setState((prev) => ({
                    ...prev,
                    RoomTypes: roomTypesData,
                }));

                setLoadedFlag(true);
                loader(false);

            } catch (error) {
                onError(error);
                loader(false);
            }

        })()
    }, [])

    const rpesLoadedRef = useRef(false);
    useEffect(() => {
        if (rpesLoadedRef.current) return;
        rpesLoadedRef.current = true;
        (async () => {
            try {
                loader(true);

                if (!state.RatePricingElements) {
                    const ratePricingElements = await Rates.GetRatePricingElements({
                        RateId: state.Id, IncludeDummies: true,
                        includes: ['IsMappedInChannelManager']
                    });
                    const derivedRateRPEs = isDerived ? await Rates.GetRatePricingElements({
                        IgnoreBaseRate: true, RateId: state.Id,
                        IncludeDummies: true, includes: ['IsMappedInChannelManager']
                    }) : [];
                    const derivedRateRPEsExcludingExisiting = derivedRateRPEs.filter(drr => !ratePricingElements.map(rpe => rpe.Id).includes(drr.Id))
                    const combinedRPEs = [...ratePricingElements, ...derivedRateRPEsExcludingExisiting];
                    setState((prev) => {
                        // Check again inside setState to avoid overwriting if already set by another effect
                        if (prev.RatePricingElements) {
                            return prev;
                        }
                        return { ...prev, RatePricingElements: combinedRPEs };
                    });
                }

                setLoadedFlag(true);
                loader(false);

            } catch (error) {
                onError(error);
                loader(false);
            }

        })()
    }, [])

    // Fix missing Package RPEs and addon-to-package links when RPEs are already loaded and packages become available
    const missingPackagesFixApplied = useRef(false);
    useEffect(() => {
        if (missingPackagesFixApplied.current) return;
        if (state.RatePricingElements?.length > 0 && packages?.length > 0) {
            // Check if there are any missing elements to add
            // Only consider active, non-override RPEs to avoid re-adding deleted packages
            const referencedPackageIds = [...new Set(
                state.RatePricingElements.filter(rpe => rpe.IsActive && !rpe.IsOverride)
                    .flatMap(rpe => rpe.RatePricingElementsPackages?.map(rpep => rpep.PackageId) || [])
            )];
            const existingPackageRPEIds = new Set(
                state.RatePricingElements.filter(rpe => rpe.EntityTypeId === EntityTypes.Package && rpe.IsActive).map(rpe => rpe.EntityId)
            );
            
            const hasMissingPackageRPEs = referencedPackageIds.some(
                pid => !existingPackageRPEIds.has(pid) && packages.some(p => p.Id === pid)
            );
            
            const hasMissingAddonLinks = [...existingPackageRPEIds].some(packageId => {
                const currentPackage = packages.find(p => p.Id === packageId);
                const packageAddonElements = currentPackage?.PackageElements?.filter(pe => pe.EntityTypeId === EntityTypes.Addon) || [];
                return packageAddonElements.some(ra => !state.RatePricingElements.some(rpe =>
                    rpe.EntityTypeId === EntityTypes.Addon &&
                    rpe.EntityId === ra.EntityId &&
                    rpe.IsActive &&
                    !rpe.IsOverride &&
                    rpe.RatePricingElementsPackages?.some(rpep => rpep.PackageId === packageId))
                );
            });
            
            if (hasMissingPackageRPEs || hasMissingAddonLinks) {
                missingPackagesFixApplied.current = true;
                setState(prev => addMissingPackagesRPEsToState(prev.RatePricingElements, prev));
            }
        }
    }, [state.RatePricingElements, packages])

    const getStartEndDates = (group) => {
        if (!group || !group[0]) {
            return [null, null];
        }

        let startDate = new Date(group[0].StartDate);
        let endDate = new Date(group[0].EndDate);
        for (let i = 1; i < group.length; i++) {
            const currentStartDate = new Date(group[i].StartDate);
            const currentEndDate = new Date(group[i].EndDate);

            startDate = currentStartDate.getTime() < startDate.getTime() ? currentStartDate : startDate;
            endDate = currentEndDate.getTime() > endDate.getTime() ? currentEndDate : endDate;
        }

        return [startDate, endDate];
    }

    useEffect(() => {
        const baseRateId = state.BaseRateId;
        const activeRoomTypeAndAddonRpes = (state.RatePricingElements || [])
            .filter(rpe =>
                rpe.EntityTypeId !== EntityTypes.Package &&
                rpe.IsActive)
            .map((rpe) =>
                ({ ...rpe, IsPackageElement: rpe.RatePricingElementsPackages?.length > 0 }));

        let displayRPEs = activeRoomTypeAndAddonRpes;
        if (baseRateId) {
            // We need to get entityIds for roomTypes and standalone addons that were deleted(IsOverride = true)
            // to filter out related base rate rpes because they have IsOverride = false and we don't want to display them
            const isOverriddenRoomType = (rpe) =>
                rpe.EntityTypeId === EntityTypes.RoomType && rpe.IsOverride;

            const isOverriddenAddonWithoutPackage = (rpe) =>
                rpe.EntityTypeId === EntityTypes.Addon && rpe.IsOverride && !rpe.IsPackageElement;

            const overriddenRoomTypeIds = new Set(
                displayRPEs.filter(isOverriddenRoomType).map(rpe => rpe.EntityId)
            );

            const overriddenAddonWithoutPackageIds = new Set(
                displayRPEs.filter(isOverriddenAddonWithoutPackage).map(rpe => rpe.EntityId)
            );

            const shouldKeepRoomTypeOrStandaloneAddonRpe = (rpe) => {
                if (rpe.RateId !== baseRateId) return true;

                if (rpe.EntityTypeId === EntityTypes.RoomType && overriddenRoomTypeIds.has(rpe.EntityId)) {
                    return false;
                }

                if (
                    rpe.EntityTypeId === EntityTypes.Addon &&
                    !rpe.IsPackageElement &&
                    overriddenAddonWithoutPackageIds.has(rpe.EntityId)
                ) {
                    return false;
                }

                return true;
            }

            displayRPEs = displayRPEs.filter(shouldKeepRoomTypeOrStandaloneAddonRpe);

            //Filter out package addons that were deleted in all packages
            const shouldKeepPackageAddonRpe = (rpe) => {
                if (rpe.IsOverride) return false;
                if (rpe.EntityTypeId === EntityTypes.Addon && addonIsOverridenInAllPackages(rpe)) return false;
                return true;
            }

            displayRPEs = displayRPEs.filter(shouldKeepPackageAddonRpe);
        }

        let groupedData = groupByIntoArray(displayRPEs, "EntityId", "EntityTypeId", "IsPackageElement");

        if (state.RateCategoryId === RateCategory.Supplement && roomTypes.length > 0) {
            let standardRoomType = roomTypes.find(x => x.DisplayOrder === 0);
            if (!standardRoomType) {
                if (!loadedFlag) {
                    onWarning(message("SupplementRateRequiresBaseLevelRoom"));
                }
                setLoadedFlag(true);
                return;
            }
            let standardRoomRPE = displayRPEs.find(x => x.EntityTypeId === EntityTypes.RoomType && x.EntityId === standardRoomType.Id)
                || {
                Id: 0,
                RateId: state.Id,
                IsActive: true,
                EntityId: standardRoomType.Id,
                EntityTypeId: EntityTypes.RoomType,
                RatePricingTypeId: RatePricingTypesEnum.PerPerson,
                StartDate: state.StartDate,
                EndDate: state.EndDate,
                Weekdays: 127,
                IsFake: true
            };
            standardRoomRPE.IsSupplement = true;
            if (displayRPEs && displayRPEs[0] && getStartEndDates) {
                const standardRoomRPEs = displayRPEs.filter(x => x.EntityTypeId === EntityTypes.RoomType && x.EntityId === standardRoomType.Id);
                const [startDate, endDate] = getStartEndDates(standardRoomRPEs);
                standardRoomRPE.StartDate = startDate;
                standardRoomRPE.EndDate = endDate;
            }

            setState(prev => {
                if (!prev.RatePricingElements) return prev;

                const rpes = prev.RatePricingElements;
                const hasActiveStandardRoomRPE = rpes.some(x =>
                    x.EntityTypeId === EntityTypes.RoomType &&
                    x.EntityId === standardRoomType.Id &&
                    x.IsActive !== false);

                if (standardRoomRPE.Id === 0 && !hasActiveStandardRoomRPE) {
                    return { ...prev, RatePricingElements: [...rpes, standardRoomRPE] };
                }

                let hasChanges = false;
                const nextRPEs = rpes.map(item => {
                    if (item.EntityTypeId === EntityTypes.RoomType &&
                        item.EntityId === standardRoomType.Id &&
                        item.IsSupplement !== true) {
                        hasChanges = true;
                        return { ...item, IsSupplement: true };
                    }
                    return item;
                });

                return hasChanges ? { ...prev, RatePricingElements: nextRPEs } : prev;
            });

            groupedData = [...groupedData.filter(group => group[0].EntityTypeId === EntityTypes.Addon), [standardRoomRPE]];
        }
        if (groupedData.length) {
            setGridData(groupedData.map((group) => {
                let rpes = group;
                let priceElement = group[0];

                if (baseRateId) {
                    // We need rpes before override filtration to get base rate rpes
                    // to get correct Start and End Dates
                    rpes = activeRoomTypeAndAddonRpes
                        .filter(rpe =>
                            (
                                rpe.RateId === baseRateId ||
                                (rpe.RateId === state.Id && !rpe.IsDummy && !rpe.IsOverride)
                            ) &&
                            rpe.EntityId === group[0].EntityId &&
                            rpe.EntityTypeId === group[0].EntityTypeId &&
                            rpe.IsPackageElement === group[0].IsPackageElement);

                    const isAddon = group[0].EntityTypeId === EntityTypes.Addon;
                    // For addon rpe we should use base rate rpe to see
                    if (isAddon) {
                        priceElement = rpes.find(rpe => rpe.RateId === baseRateId) ?? group[0];
                    }

                    if (rpes.length === 0) {
                        rpes = group
                    }
                }

                const isRoomTypeAddon = priceElement.EntityTypeId === EntityTypes.Addon &&
                    addons.find(a => a.Id === priceElement.EntityId)?.RoomTypeAddons?.length > 0;

                const [startDate, endDate] = getStartEndDates(rpes);
                return ({
                    Type: text(priceElement.EntityTypeId === EntityTypes.Addon ? "AddOn" : "RoomType"),
                    RateElementName: getElementName(priceElement.EntityTypeId, priceElement.EntityId, getAddonOccurrencesInNotOverridenPackages(priceElement)),
                    IsPackage: priceElement.RatePricingElementsPackages?.length > 0,
                    IsRoomTypeAddon: isRoomTypeAddon,
                    RateElement: getElement(priceElement.EntityTypeId, priceElement.EntityId),
                    PricingType: priceElement,
                    PricingTemplate: priceElement,
                    Dates: `${dateFormatter(startDate)} - ${dateFormatter(endDate)}`,
                    StartDate: startDate,
                    EndDate: endDate,
                    Edit: priceElement
                });
            }));
        }
        else {
            setGridData([]);
        }

    }, [state.RatePricingElements, loadedFlag, roomTypes, addons])

    const setSelectedEntityWithoutSpecificFields = (rpe) => {
        const selectedEntities = cloneEntityWithoutSpecificFields(rpe, [
            'Id',
            'RatePricingElementId',
            'RatePerPersonId',
            'HotelAddonId',
        ])
        setSelectedEntity(selectedEntities)
    }

    const getElement = (entityTypeId, entityId) => {
        switch (entityTypeId) {
            case EntityTypes.RoomType:
                return roomTypes.find(rt => rt.Id === entityId);
            case EntityTypes.Package:
                return packages.find(rt => rt.Id === entityId);
            case EntityTypes.Addon:
                return addons.find(rt => rt.Id === entityId);
            default:
                return null;
        }
    }

    const getElementName = (entityTypeId, entityId, packagesCount) => {
        let packagesString = '';
        let shortNameString = '';
        switch (entityTypeId) {
            case EntityTypes.RoomType:
                shortNameString = roomTypes.find(rt => rt.Id === entityId)?.ShortName;
                return `${roomTypes.find(rt => rt.Id === entityId)?.Name} ${shortNameString ? `(${shortNameString})` : ""}`;
            case EntityTypes.Package:
                return packages.find(rt => rt.Id === entityId)?.Name;
            case EntityTypes.Addon:
                packagesString = packagesCount ? packagesCount === 1 ? `(${text("Package")})` : `(${packagesCount} ${text("Packages")})` : ""
                return `${addons.find(rt => rt.Id === entityId)?.Name} ${packagesString}`;
            default:
                return "";
        }
    }


    const dateFormatter = (value) => {
        return value ? moment(value).format(dateFormat) : "";
    };

    const rateElementNameFormatter = ({ value: rpe, row }) => {
        if (state.RateCategoryId === RateCategory.ExtraAddOn &&
            row.IsRoomTypeAddon) {
            return (
                <span className="is-room-type-addon">
                    {rpe}
                </span>
            );
        }

        return rpe;
    };

    const editFormatter = ({ value: rpe }) => {
        let isDisabled = state.RateCategoryId === RateCategory.Supplement;

        const getIconColor = (rpe, supplement) => {
            const incomplete = "orange"
            if (supplement) {
                const standardRoomType = roomTypes.find(x => x.DisplayOrder === 0);
                const elements = state.RatePricingElements
                    .filter(x => x.EntityTypeId === EntityTypes.RoomType && x.EntityId !== standardRoomType?.Id);
                const coverage = isCovered(state, elements);
                return coverage.hasGap ? incomplete : null;
            }

            const hasPrices = (rpe.IsDummy ||
                rpe.RatePerPeople?.length +
                rpe.RatePerRooms?.length +
                rpe.RatePerCombinations?.length +
                rpe.HotelAddons?.filter(ha => ha.HotelAddonsPrices?.length > 0)?.length) > 0;
            if (!hasPrices) {
                return "red";
            } else {
                const elements = state.RatePricingElements?.filter(r =>
                    r.EntityTypeId === rpe.EntityTypeId &&
                    r.EntityId === rpe.EntityId &&
                    r.RateId === state.Id &&
                    r.IsActive &&
                    !r.IsDummy);

                // In case of derived rate that doesn't have any pricing elements of its own
                // We skip the check
                if (state.BaseRateId && (!elements || elements.length === 0)) {
                    return null;
                }
                const coverage = isCovered(state, elements);
                return coverage.hasGap ? incomplete : null;
            }
        }

        return (
            <span>
                <Popup
                    disabled={isDisabled}
                    content={text("Delete")}
                    trigger={
                        <Icon
                            link
                            onClick={() => deleteEntity(rpe)}
                            disabled={isDisabled || !!rpe.RatePricingElementsPackages?.length}
                            name={isDisabled ? 'trash alternate disabled' : 'trash alternate'}
                            size='large'
                        />
                    }
                />
                <Popup
                    content={text("Prices")}
                    trigger={
                        <Icon
                            link
                            onClick={() => editPrices(rpe)}
                            name={'calendar alternate outline'}
                            color={getIconColor(rpe)}
                            size='large'
                        />
                    }
                />
                {rpe.EntityTypeId === EntityTypes.RoomType &&
                    rpe.RatePricingTypeId === RatePricingTypesEnum.PerPerson && (
                        <Popup
                            content={text("Combinations")}
                            trigger={
                                <Icon
                                    link
                                    onClick={() => editCombinations(rpe)}
                                    name={'table'}
                                    size='large'
                                />
                            }
                        />
                    )}
                {rpe.IsSupplement &&
                    state.RatePricingElements?.find(x => x.IsActive) && (
                        <Popup
                            content={text("EditSupplementPrices")}
                            trigger={
                                <Icon
                                    link
                                    color={getIconColor(rpe, true)}
                                    onClick={() => editSupplement(rpe)}
                                    name="plus"
                                    size="large"
                                />
                            }
                        />
                    )}
                {rpe.EntityTypeId === EntityTypes.Addon && (
                    <Popup
                        content={text("EditAddonOptions")}
                        trigger={
                            <Icon
                                link
                                onClick={() => editAddonOptions(rpe)}
                                name={'list alternate outline'}
                                size='large'
                            />
                        }
                    />
                )}
            </span>
        )
    }

    const priceTypeFormatter = ({ value: rpe }) => {
        return <DropDown className="pricing-template-dropdown"
            key="Id" text={text(RateTypes[rpe.RatePricingTypeId])} textField="Name" valueField="Id"
            data={RateTypeDropDownData.filter(rt =>
                rpe.EntityTypeId !== EntityTypes.Addon ||
                [RatePricingTypesEnum.PerRoom, RatePricingTypesEnum.PerPerson].includes(rt.Id)
            )}
            value={rpe.RatePricingTypeId} onChange={(value) => updatePricingElementProp(rpe, 'RatePricingTypeId', value)}
            disabled={isPricingTypeDisabled(rpe)} />
    }

    const pricingTemplateFormatter = ({ value: rpe }) => {
        return pricingTemplates && <DropDown
            className="pricing-template-dropdown"
            clearable
            style={{ position: 'absolute', marginTop: '-15px', width: '14em' }}
            key="PricingTemplateId"
            textField="Name"
            valueField="Id"
            data={pricingTemplates}
            disabled={rpe.EntityTypeId === EntityTypes.Addon || rpe.RatePricingTypeId !== RatePricingTypesEnum.PerPerson || state.BaseRateId}
            value={rpe?.PricingTemplateId}
            onChange={(value) => updatePricingElementProp(rpe, 'PricingTemplateId', value || null)} />
    }

    const pricingTypePredicate = (row, filter) => {
        const pricingTypeName = RateTypeDropDownData.find(rt => rt.Id === row?.RatePricingTypeId)?.Name;
        return pricingTypeName?.toLocaleLowerCase().includes(filter.value.toLocaleLowerCase());
    }

    const pricingTemplatePredicate = (row, filter) => {
        const pricingTemplateName = pricingTemplates.find(pt => pt.Id === row?.PricingTemplateId)?.Name;
        return pricingTemplateName?.toLocaleLowerCase().includes(filter.value.toLocaleLowerCase());
    }

    const updatePricingElementProp = (rpe, propertyName, value) => {
        var currentPricingElements = [...state.RatePricingElements];
        for (let i = 0; i < currentPricingElements.length; i++) {
            if ((currentPricingElements[i].EntityId === rpe.EntityId &&
                currentPricingElements[i].EntityTypeId === rpe.EntityTypeId &&
                currentPricingElements[i].RatePricingElementsPackages?.length == rpe.RatePricingElementsPackages?.length)
                || //Updating other roomTypes ratePricingTypeId
                (propertyName === 'RatePricingTypeId' &&
                    rpe.EntityTypeId === EntityTypes.RoomType &&
                    currentPricingElements[i].EntityTypeId === EntityTypes.RoomType)
                || //Updating other roomTypes template in supplement
                (propertyName === 'PricingTemplateId' && rpe.IsSupplement &&
                    rpe.EntityTypeId === EntityTypes.RoomType &&
                    currentPricingElements[i].EntityTypeId === EntityTypes.RoomType)
            ) {
                currentPricingElements[i][propertyName] = value;
            }
        }
        setState((prev) => ({ ...prev, RatePricingElements: currentPricingElements }));
        setDirtyState(prev => ({ ...prev, PricingTab: true }));
    }

    const isPricingTypeDisabled = (rpe) => {
        if (rpe.EntityTypeId == EntityTypes.RoomType) {
            return !!state?.RatePricingElements?.find(rpe => rpe.EntityTypeId == EntityTypes.RoomType && elementHasPricing(rpe));
        }
        else return elementHasPricing(rpe);
    }

    const elementHasPricing = (rpe) => {
        return rpe.IsSupplement ||
            rpe.IsDummy ||
            !!(rpe.RatePerCombinations?.length ||
                rpe.RatePerPeople?.length ||
                rpe.RatePerRooms?.length ||
                rpe.HotelAddons?.filter(ha => ha.HotelAddonsPrices?.length > 0)?.length);
    }

    const editSupplement = (rpe) => {
        setSelectedEntityWithoutSpecificFields(rpe);
        setShowSupplementPopup(true);
    }

    const editPrices = (rpe) => {
        setSelectedEntity(rpe);
        let RPEType =
            rpe.EntityTypeId == EntityTypes.Addon
                ?
                RatePricingTypesEnum.Addon
                :
                rpe.RatePricingTypeId
        switch (RPEType) {
            case RatePricingTypesEnum.PerPerson:
                setShowPricingPerPersonPopup(true);
                break;
            case RatePricingTypesEnum.PerRoom:
                setShowPricingPerRoomPopup(true);
                break;
            case RatePricingTypesEnum.FixPerPax:
                setShowPricingPerVillaPopup(true);
                break;
            case RatePricingTypesEnum.Addon:
                rpe.RatePricingTypeId === RatePricingTypesEnum.PerPerson ? setShowPersonAddonPopup(true) : setShowRoomAddonPopup(true);
                break;
        }
    }
    const editCombinations = (rpe) => {
        setSelectedEntity(rpe);
        if (rpe.RatePricingTypeId === RatePricingTypesEnum.PerPerson)
            setShowCombinationsPopup(true);
    }

    const deleteEntity = async (value) => {
        const msg = IsRestrictedForDelete(value, null, true) ? message("DeleteRateWhenIsMappedToChannel") : message("AreYouSure")

        switch (value.EntityTypeId) {
            case EntityTypes.RoomType:
                await deleteWithYesNoModal(value, deleteRoomType, msg);
                break;
            case EntityTypes.Addon:
                await deleteWithYesNoModal(value, deleteAddon, msg);
                break;
            case EntityTypes.Package:
                await deleteWithYesNoModal(value, deletePackage, msg);
                break;
            default:
                break;
        }
    };

    const buildRatePricingElements = (entityTypeId, entityId, ratePricingElementsPackages, isDummy, ratePricingTypeId) => {
        const startDate = state.StartDate || Date();
        const endDate = state.EndDate || Date();
        const result = []

        // In case of supplement, we need to add the new RPE to existing date ranges that are currently present
        // in the state. This is to ensure that the new RPE is added to all the date ranges of the existing RPEs.
        if (entityTypeId == EntityType.RoomType && state.RateCategoryId === RateCategory.Supplement) {
            const standardRoomTypeId = roomTypes.find(x => x.DisplayOrder === 0)?.Id

            const BaseRpe = state.RatePricingElements.find(rpe =>
                rpe.IsActive &&
                rpe.EntityTypeId === EntityTypes.RoomType &&
                rpe.EntityId === standardRoomTypeId)

            const RatePricingElements = state.RatePricingElements.filter(
                (rpe) =>
                    rpe.EntityTypeId === EntityTypes.RoomType &&
                    rpe.IsActive &&
                    rpe.EntityId !== standardRoomTypeId
            );

            // Create a Set to store unique ranges
            const uniqueRanges = new Set();
            if (RatePricingElements.length === 0) {
                RatePricingElements.push({
                    StartDate: state.StartDate,
                    EndDate: state.EndDate,
                    Weekdays: 127, // All weekdays
                });
            }

            // Iterate over all RPEs and collect unique ranges
            RatePricingElements.forEach((rpe) => {
                const rangeKey = `${rpe.StartDate}$${rpe.EndDate}$${rpe.Weekdays}`;
                if (uniqueRanges.has(rangeKey)) return; // Skip if already exists
                uniqueRanges.add(rangeKey);
            });

            // Create new RPEs for each unique range
            const newRPEs = Array.from(uniqueRanges).map((rangeKey) => {
                const [startDate, endDate, weekdays] = rangeKey.split("$");
                return {
                    TempId: `newPricingTempId-${crypto.randomUUID()}`,
                    RateId: state.Id,
                    EntityTypeId: entityTypeId,
                    EntityId: entityId,
                    PricingTemplateId: BaseRpe?.PricingTemplateId,
                    RatePricingTypeId: ratePricingTypeId || state.RatePricingElements?.find(x => x.EntityTypeId === entityTypeId)?.RatePricingTypeId || RatePricingTypesEnum.PerPerson,
                    RatePricingElementsPackages: ratePricingElementsPackages,
                    HotelAddons: [],
                    RatePerPeople: [],
                    RatePerCombinations: [],
                    RatePerRooms: [],
                    StartDate: startDate,
                    EndDate: endDate,
                    Weekdays: parseInt(weekdays, 10),
                    IsActive: true,
                    IsDummy: isDummy,
                    IsNew: true
                };
            });
            result.push(...newRPEs);
        }
        else {
            // If adding an Addon, initialize HotelAddons with chain-level IsDistributeToChannelManager
            let hotelAddons = [];
            if (entityTypeId === EntityTypes.Addon) {
                // Find the chain-level addon for this entityId
                const chainAddon = addons.find(a => a.Id === entityId);
                if (chainAddon) {
                    hotelAddons = [{
                        Id: 0,
                        RatePricingElementId: 0,
                        RateId: state.Id,
                        IsActive: true,
                        CurrencyId: state.CurrencyId || currentHotel?.MainCurrencyId,
                        HotelAddonsPrices: [],
                        HotelId: state.HotelId,
                        HotelChainId: state.HotelChainId,
                        StartDate: startDate,
                        EndDate: endDate,
                        ChainAddonId: entityId,
                        IsDistributeToChannelManager: chainAddon.IsDistributeToChannelManager
                    }];
                }
            }
            result.push({
                TempId: `newPricingTempId-${crypto.randomUUID()}`,
                RateId: state.Id,
                EntityTypeId: entityTypeId,
                EntityId: entityId,
                RatePricingTypeId: ratePricingTypeId || state.RatePricingElements?.find(x => x.EntityTypeId === entityTypeId)?.RatePricingTypeId || RatePricingTypesEnum.PerPerson,
                RatePricingElementsPackages: ratePricingElementsPackages,
                HotelAddons: hotelAddons,
                RatePerPeople: [],
                RatePerCombinations: [],
                RatePerRooms: [],
                StartDate: startDate,
                EndDate: endDate,
                Weekdays: makeDaysOfWeekArrayIntoInt(getWeekDaysOfRange(startDate, endDate, AllWeekDays)),
                IsActive: true,
                IsDummy: isDummy,
                IsNew: true
            })
        }
        return result
    }

    const addRatePricingElementToState = (
        currentState,
        entityTypeId,
        entityId,
        packageId
    ) => {
        if (packageId) {
            const existingElement = currentState.RatePricingElements?.find(
                (rpe) =>
                    rpe.EntityTypeId === entityTypeId &&
                    rpe.EntityId === entityId &&
                    rpe.RatePricingElementsPackages?.length &&
                    rpe.IsActive
            )

            if (existingElement) {
                const relevantRpes = currentState.RatePricingElements.filter(
                    (x) =>
                        x.EntityId === entityId &&
                        x.EntityTypeId == entityTypeId &&
                        x.RatePricingElementsPackages?.length &&
                        x.IsActive
                );

                for (let i = 0; i < relevantRpes.length; i++) {
                    const currentRpe = relevantRpes[i];

                    if (!currentRpe.RatePricingElementsPackages.find((x) => x.PackageId === packageId)) {
                        if (currentRpe.RateId === state.Id) {
                            currentRpe.RatePricingElementsPackages.push({
                                PackageId: packageId,
                                RatePricingElementId: relevantRpes[i].Id,
                            });
                        } else {
                            currentState = {
                                ...currentState,
                                RatePricingElements: [
                                    ...buildRatePricingElements(
                                        entityTypeId,
                                        entityId,
                                        [{ PackageId: packageId }],
                                        true,
                                        existingElement.RatePricingTypeId
                                    ),
                                    ...currentState.RatePricingElements,
                                ],
                            };
                        }
                    }
                }
                return currentState;
            } else {
                return {
                    ...currentState,
                    RatePricingElements: [
                        ...buildRatePricingElements(entityTypeId, entityId, [
                            { PackageId: packageId },
                        ]),
                        ...currentState.RatePricingElements,
                    ],
                }
            }
        } else {
            return {
                ...currentState,
                RatePricingElements: [
                    ...buildRatePricingElements(entityTypeId, entityId),
                    ...currentState.RatePricingElements,
                ],
            }
        }
    }

    const availableRoomTypes = useMemo(() => {
        const roomTypePricingElements = state.RatePricingElements?.filter(rpe => rpe.EntityTypeId === EntityTypes.RoomType) ?? [];
        const addedRoomTypeIdsSet = new Set([...roomTypePricingElements.map(rpe => rpe.EntityId)]);
        return roomTypes.filter(rt =>
            !(state.RateCategoryId === RateCategory.Supplement && rt.DisplayOrder === 0) &&
            !addedRoomTypeIdsSet.has(rt.Id));
    }, [state, roomTypes]);

    const addRoomType = async () => {
        let currentState = { ...state };
        const roomTypesToAdd = isAllRoomTypesSelected ? availableRoomTypes.map((rt) => rt.Id) : selectedRoomTypes;

        for (const roomTypeId of roomTypesToAdd) {
            currentState = addRatePricingElementToState(currentState, EntityTypes.RoomType, roomTypeId);
        }
        setSelectedRoomTypes([]);
        setState(currentState);
        setDirtyState(prev => ({ ...prev, PricingTab: true }));
    };

    const deleteRoomType = async (value) => {
        if (value.Id) {
            if (!rpeHasPricings(value)) {
                setSelectedRoomTypes([]);
            }

            await deleteRatePricingElementAndRefresh(value);
        }
        else {
            setSelectedRoomTypes([]);
            setState((prev) => ({ ...prev, RatePricingElements: [...prev.RatePricingElements.filter(rpe => rpe.EntityTypeId !== EntityTypes.RoomType || rpe.EntityId !== value.EntityId)] }))
        }
    };

    const rpeHasPricings = (value) => {
        return value.RatePerCombinations?.length || value.RatePerPeople?.length || value.RatePerRooms?.length || value.HotelAddons?.length;
    }

    const deleteRPEFromPopups = async (rpe, rpes, setRPEs) => {
        const msg = IsRestrictedForDelete(rpe, rpes) ? message("DeleteRateWhenIsMappedToChannel") : message("AreYouSure")

        if (rpe.Id) {
            await deleteWithYesNoModal(rpe, async (value) => {
                if (isDerived && value.RateId === state.BaseRateId) {
                    rpes.push({
                        ...value,
                        RatePerPeople: [],
                        RatePerCombinations: [],
                        RatePerRooms: [],
                        HotelAddons: [],
                        RateId: state.Id,
                        IsOverride: true,
                        IsActive: true,
                        Id: 0
                    });
                    rpes = rpes.filter(x => x.Id !== value.Id);
                }
                else {
                    value.IsActive = false;
                }
                setRPEs([...rpes]);
            }, msg);
        }
        else {
            setRPEs(rpes.filter((p) => p !== rpe));
        }
    }

    const getRPEsIndicatingDeletionOfDerrived = (rpes) => {
        return rpes.map(rpe => ({
            ...rpe,
            RatePerPeople: [],
            RatePerCombinations: [],
            RatePerRooms: [],
            HotelAddons: [],
            RateId: state.Id,
            IsOverride: true,
            IsActive: true,
            Id: 0
        }))
    }

    const IsRestrictedForDelete = (rpe, rpes = null, isDeleteAll = false) => {
        const rpesForValidation = rpes || state.RatePricingElements;
        const isLastEntity = rpesForValidation.filter(x => x.EntityId == rpe.EntityId &&
            x.EntityTypeId == rpe.EntityTypeId && !x.IsDummy && x.IsActive).length == 1;
        if (rpe.IsMappedInChannelManager && (isLastEntity || isDeleteAll)) {
            return true;
        }
        else return false;
    }
    const deleteRatePricingElementAndRefresh = async (value) => {
        try {
            loader(true);
            let rpesForDeletion = state.RatePricingElements?.filter(rpe => rpe.EntityId == value.EntityId &&
                rpe.EntityTypeId == value.EntityTypeId &&
                rpe.RatePricingElementsPackages?.length == value.RatePricingElementsPackages?.length);
            if (value.EntityTypeId === EntityTypes.Package) {
                state.RatePricingElements?.forEach(rpe => {
                    if (rpe.RateId === state.Id && rpe.RatePricingElementsPackages?.find(pack => pack.PackageId === value.EntityId)) {
                        rpe.RatePricingElementsPackages = rpe.RatePricingElementsPackages.filter(pack => pack.PackageId !== value.EntityId);
                        if (rpe.RatePricingElementsPackages.length === 0)
                            rpesForDeletion.push(rpe);
                    }
                })
            }
            let derivedRPEsForDeletion = rpesForDeletion.filter(x => x.RateId === state.BaseRateId);
            if (value.EntityTypeId == EntityTypes.Package) {
                derivedRPEsForDeletion = derivedRPEsForDeletion.filter(rpe => rpe.EntityTypeId == EntityTypes.Package);
            }
            let RPEsThatIndicateDeletionOfDerived = getRPEsIndicatingDeletionOfDerrived(derivedRPEsForDeletion);
            await PriceLists.UpdatePricingElements({
                StartDate: state.StartDate,
                EndDate: state.EndDate,
                HotelId: state.HotelId,
                RatePricingElements: [
                    ...state.RatePricingElements,
                    ...RPEsThatIndicateDeletionOfDerived
                ],
                CurrencyId: state.CurrencyId || currentHotel?.MainCurrencyId,
                IsProductPricingIncludesTax: state?.IsProductPricingIncludesTax,
                IsDistributeToChannelManager: state.IsDistributeToChannelManager,
                RateCategoryId: state.RateCategoryId,
                Id: state.Id,
                BaseRateId: state.BaseRateId
            });

            let nativeRPEsForDeletion = rpesForDeletion.filter(x => x.RateId === state.Id);
            nativeRPEsForDeletion.forEach((rpe) => rpe.IsActive = false);
            await PriceLists.UpdatePricingElements({
                StartDate: state.StartDate,
                EndDate: state.EndDate,
                HotelId: state.HotelId,
                RatePricingElements: nativeRPEsForDeletion.map(rpe => cloneEntityWithoutSpecificFields(rpe, ["IsActive"], false)),
                CurrencyId: state.CurrencyId || currentHotel?.MainCurrencyId,
                IsProductPricingIncludesTax: state?.IsProductPricingIncludesTax,
                IsDistributeToChannelManager: state.IsDistributeToChannelManager,
                RateCategoryId: state.RateCategoryId,
                Id: state.Id,
                BaseRateId: state.BaseRateId
            });
            refreshRPEs();
        }
        catch (error) {
            onError(error);
        }
        finally {
            loader(false);
        }
    };


    const deleteWithYesNoModal = async (value, onConfirmCallback, message) => {
        await confirm({
            header: `${text('Delete')} ${text('RateElement')}`,
            content: message,
            confirmButton: {
                content: text('Yes'),
                positive: true,
            },
            cancelButton: {
                content: text('No'),
                negative: true,
            },
            onConfirm: async () => onConfirmCallback(value)
        });
    };

    const roomTypeDropdownOptions = useMemo(() => {
        return isAllRoomTypesSelected ? [allOption] : [allOption, ...availableRoomTypes];
    }, [availableRoomTypes, allOption]);

    const handleRoomTypeSelected = useCallback((values) => {
        if (values?.some(value => value === ALL_OPTION_ID) || values?.length === availableRoomTypes.length) {
            setSelectedRoomTypes([ALL_OPTION_ID]);
        } else {
            setSelectedRoomTypes(values ?? []);
        }
    }, [availableRoomTypes]);


    const addPackage = () => {
        var currentState = { ...state };
        const packagesToAdd = isAllPackagesSelected ? availablePackages.map(p => p.Id) : selectedPackages;
        for (const packageId of packagesToAdd) {
            currentState = addPackageAndRelatedAddons(packageId, currentState);
        }

        setState(currentState);
        setDirtyState(prev => ({ ...prev, PricingTab: true }));
    }

    const addPackageAndRelatedAddons = (packageId, currentState) => {
        const addonIds = packages.find(p => p.Id === packageId)?.PackageElements.filter(pe => pe.EntityTypeId === EntityTypes.Addon).map(pe => pe.EntityId);

        if (addonIds?.length) {
            setSelectedAddons([]);
            for (let i = 0; i < addonIds.length; i++) {
                currentState = addRatePricingElementToState(currentState, EntityTypes.Addon, addonIds[i], packageId);
            }
        }
        setSelectedPackages([]);
        if (state.RatePricingElements?.find(rpe => rpe.EntityTypeId === EntityTypes.Package && rpe.IsActive && rpe.EntityId === packageId)) {
            return currentState;
        }
        else return addRatePricingElementToState(currentState, EntityTypes.Package, packageId);
    }

    const deletePackage = async (value) => {
        setSelectedPackages([]);
        if (value.Id) {
            deleteRatePricingElementAndRefresh(value);
        }
        else {
            var rpesClone = [...state.RatePricingElements];
            rpesClone.forEach((element) => {
                if (element.RateId === state.Id && element.RatePricingElementsPackages?.length) {
                    element.RatePricingElementsPackages = element.RatePricingElementsPackages.filter(rpep => rpep.PackageId !== value.EntityId);
                    if (element.RatePricingElementsPackages.length === 0) //if this element was part of the package and nothing else, it should be deleted with the deletion of the package
                        element.IsActive = false;
                }
            })

            rpesClone = rpesClone.filter((rpe) => !(rpe.EntityTypeId === EntityTypes.Package && rpe.EntityId === value.EntityId));

            setState((prev) => ({ ...prev, RatePricingElements: rpesClone }));
        }
    };

    const availablePackages = useMemo(() => {
        const addedPackagesIds = state.RatePricingElements?.flatMap(rpe =>
            rpe.RatePricingElementsPackages?.map(rpep => rpep.PackageId) || []
        ) ?? [];
        const addedPackageIdsSet = new Set(addedPackagesIds);

        return packages.filter(p => !addedPackageIdsSet.has(p.Id) && !(!p.PackageElements || p.PackageElements.length === 0));
    }, [state, packages]);

    const packageDropdownOptions = useMemo(() => {
        return isAllPackagesSelected ? [allOption] : [allOption, ...availablePackages];
    }, [availablePackages, isAllPackagesSelected, allOption]);

    const handlePackageSelected = useCallback((values) => {
        if (values?.some(value => value === ALL_OPTION_ID) || values?.length === availablePackages.length) {
            setSelectedPackages([ALL_OPTION_ID]);
        } else {
            setSelectedPackages(values ?? []);
        }
    }, [availablePackages]);

    const addAddon = async () => {
        var currentState = { ...state };
        const addonsToAdd = isAllAddonsSelected ? availableAddons.map(a => a.Id) : selectedAddons;
        for (const addonId of addonsToAdd) {
            currentState = addRatePricingElementToState(currentState, EntityTypes.Addon, addonId);
        }
        setSelectedAddons([]);
        setState(currentState);
        setDirtyState(prev => ({ ...prev, PricingTab: true }));
    }
    const deleteAddon = async (value) => {
        if (value.Id) {
            if (!rpeHasPricings(value)) {
                setSelectedAddons([]);
            }

            await deleteRatePricingElementAndRefresh(value);
        }
        else {
            setSelectedAddons([]);
            setState((prev) => ({
                ...prev, RatePricingElements: [...state.RatePricingElements?.filter(rpe => rpe.EntityTypeId !== EntityTypes.Addon ||
                    rpe.EntityId !== value.EntityId ||
                    (rpe.EntityId === value.EntityId && rpe.RatePricingElementsPackages?.length))]
            }))
        }

    }
    const availableAddons = useMemo(() => {
        const ratePricingElements = state.RatePricingElements ?? [];
        const addonsRatePricingElements = ratePricingElements.filter(rpe => rpe.EntityTypeId === EntityTypes.Addon);
        const isAddonSelectedInRatePricingElemnts = addon => addonsRatePricingElements.some(rpe => rpe.EntityId === addon.Id);
        const addonExistsInSelectedElementsAndIncludedInSelectedPackages = addon => addonsRatePricingElements
            .some(rpe => rpe.EntityId === addon.Id && rpe.RatePricingElementsPackages?.length > 0);
        const noOccurencesOfAddonThatIsNotInSelectedPackages = addon => !addonsRatePricingElements
            .some(rpe => rpe.EntityId === addon.Id && !rpe.RatePricingElementsPackages);

        return addons.filter(addon => !isAddonSelectedInRatePricingElemnts(addon) ||
            (addonExistsInSelectedElementsAndIncludedInSelectedPackages(addon) && noOccurencesOfAddonThatIsNotInSelectedPackages(addon))).
            sort((a, b) => a.RoomTypeAddons?.length > b.RoomTypeAddons?.length ? -1 : 1)
    }, [state, addons]);

    const addonDropdownOptions = useMemo(() => {
        return isAllAddonsSelected ? [allOption] : [allOption, ...availableAddons];
    }, [availableAddons, isAllAddonsSelected, allOption]);

    const handleAddonSelected = useCallback((values) => {
        if (values?.some(value => value === ALL_OPTION_ID) || values?.length === availableAddons.length) {
            setSelectedAddons([ALL_OPTION_ID]);
        } else {
            setSelectedAddons(values ?? []);
        }
    }, [availableAddons]);

    const editAddonOptions = (pricingElement) => {
        setSelectedEntityWithoutSpecificFields(pricingElement);
        setShowAddonOptionsPopup(true);
    }

    const changeAddonOptions = async (updatedHotelAddon) => {
        let fittingPricingElements;
        if (selectedEntity.IsPackageElement) {
            fittingPricingElements = state.RatePricingElements?.filter(x => x.EntityId === selectedEntity.EntityId &&
                x.EntityTypeId === selectedEntity.EntityTypeId &&
                x.IsActive &&
                x.RatePricingElementsPackages?.length);
        }
        else {
            fittingPricingElements = state.RatePricingElements?.filter(x => x.EntityId === selectedEntity.EntityId &&
                x.EntityTypeId === selectedEntity.EntityTypeId &&
                x.IsActive &&
                !x.RatePricingElementsPackages?.length);
        }

        // Always get the parent chainAddon for the current EntityId
        const chainAddon = addons.find(a => a.Id === selectedEntity.EntityId);
        const isDistributeToChannelManager = chainAddon ? chainAddon.IsDistributeToChannelManager : false;

        for (let element of fittingPricingElements) {
            if (!element.HotelAddons || element.HotelAddons.length === 0) {
                element.HotelAddons = [{
                    Id: 0,
                    RatePricingElementId: element.Id,
                    RateId: state.Id,
                    IsActive: true,
                    CurrencyId: state.CurrencyId || currentHotel?.MainCurrencyId,
                    HotelAddonsPrices: [],
                    HotelId: state.HotelId,
                    HotelChainId: state.HotelChainId,
                    StartDate: element.StartDate,
                    EndDate: element.EndDate,
                    ChainAddonId: element.EntityId,
                    IsDistributeToChannelManager: isDistributeToChannelManager,
                    ...getHotelAddonOptions(updatedHotelAddon),
                }];
            }
            else element.HotelAddons = element.HotelAddons?.map(hotelAddon => ({ ...hotelAddon, ...getHotelAddonOptions(updatedHotelAddon) }));
        }
        try {
            loader(true);
            await PriceLists.UpdatePricingElements({
                HotelId: state.HotelId,
                RatePricingElements: fittingPricingElements,
                CurrencyId: state.CurrencyId || currentHotel?.MainCurrencyId,
                IsProductPricingIncludesTax: state?.IsProductPricingIncludesTax,
                IsDistributeToChannelManager: state.IsDistributeToChannelManager,
                RateCategoryId: state.RateCategoryId,
                Id: state.Id,
                BaseRateId: state.BaseRateId
            });
            onSuccess();
            refreshRPEs();
            setShowAddonOptionsPopup(false);
        } catch (error) {
            error.status === 400 ? onWarning(error) : onError(error)
        } finally {
            loader(false);
        }
    }

    const getHotelAddonOptions = (hotelAddon) => {
        let newOptions = {};
        for (let field of addonOptionsFields)
            newOptions[field] = hotelAddon[field];
        return newOptions;
    }

    const rateElementPredicate = useCallback((rateElementName, filter) => {
        return rateElementName && rateElementName.toLocaleLowerCase().includes(filter.value.toLocaleLowerCase())
    }, []);

    const gridOptions = {
        columns: [
            { name: 'Type', title: text('Type') },
            { name: 'RateElementName', title: text('RateElement'), formatter: rateElementNameFormatter },
            { name: 'PricingType', title: text('PricingType'), formatter: priceTypeFormatter },
            { name: 'PricingTemplate', title: text('PricingTemplate'), formatter: pricingTemplateFormatter },
            { name: 'Dates', title: text('Dates') },
            { name: 'Edit', title: text('Edit'), formatter: editFormatter }
        ],
        cacheId: 'PriceList-Pricing',
        customOptions: {
            group: {
                extensions: [
                    { columnName: 'Edit', groupingEnabled: false },
                ]
            }
        },
        filtering: {
            extensions: [
                { columnName: 'Edit', filteringEnabled: false },
                { columnName: 'RateElementName', predicate: rateElementPredicate },
                { columnName: 'PricingType', predicate: pricingTypePredicate },
                { columnName: 'PricingTemplate', predicate: pricingTemplatePredicate },
            ]
        },
        showFilter: true,
        columnExtensions: [
            { columnName: 'PricingType', width: '15em' },
        ],
        noVirtualTable: true,
        paging: {
            pageSize: 10
        }
    }


    const updateRPEs = async ({ rpes, isClose, onClose, setIsDirty, setPricings, validate }) => {
        if (validate && !(await validate())) {
            return;
        }
        setPricings(rpes => rpes.map(rpe => ({ ...rpe })));
        loader(true);
        try {
            await PriceLists.UpdatePricingElements({
                StartDate: state.StartDate,
                EndDate: state.EndDate,
                Id: state.Id,
                RatePricingElements: rpes.filter(rpe => rpe.RateId === state.Id),
                HotelId: state.HotelId,
                CurrencyId: state.CurrencyId || currentHotel?.MainCurrencyId,
                IsProductPricingIncludesTax: state?.IsProductPricingIncludesTax,
                IsDistributeToChannelManager: state?.IsDistributeToChannelManager,
                RateCategoryId: state.RateCategoryId,
                BaseRateId: state.BaseRateId
            });
            await refreshRPEs();
            onSuccess();
            setIsDirty(false);
            isClose && onClose();
        } catch (error) {
            onError(error)
        } finally {
            loader(false);
        }
    }

    const refreshRPEs = async () => {
        try {
            loader(true);
            const currentRateRPEs = await Rates.GetRatePricingElements({ RateId: state.Id, IncludeDummies: true, includes: ['IsMappedInChannelManager'] });
            setState(prev => {
                // Use prev.RatePricingElements instead of the closure's state so that RPEs
                // added/removed between the async fetch and this commit are not duplicated or lost.
                const combinedFetchedRPEs = [...currentRateRPEs];
                const unsavedRPEs = prev.RatePricingElements.filter(x => x.IsNew && x.IsActive !== false && !combinedFetchedRPEs.find(rpe => rpe.EntityTypeId === x.EntityTypeId
                    && rpe.EntityId === x.EntityId && rpe.IsDummy === x.IsDummy)
                );

                const unsavedRatePricingElementPackagesRPES = prev.RatePricingElements.filter(rpe => !rpe.IsNew && rpe.RatePricingElementsPackages?.find(rpep => !rpep.Id));
                for (let i = 0; i < combinedFetchedRPEs.length; i++) {
                    for (let j = 0; j < unsavedRatePricingElementPackagesRPES.length; j++) {
                        if (combinedFetchedRPEs[i].Id === unsavedRatePricingElementPackagesRPES[j].Id) {
                            combinedFetchedRPEs[i].RatePricingElementsPackages = unsavedRatePricingElementPackagesRPES[j].RatePricingElementsPackages
                        }
                    }
                }

                const rpes = [...unsavedRPEs, ...currentRateRPEs];
                let updatedState = { ...prev, RatePricingElements: rpes };
                updatedState = addMissingPackagesRPEsToState(rpes, updatedState);
                return updatedState;
            });
        } catch (error) {
            error.status === 400 ? onWarning(error) : onError(error);
        } finally {
            loader(false);
        }
    }

    // When the main Save button is clicked, index.jsx sets forceRefreshRPEs to signal that
    // stale Id:0 RPEs have been persisted and DB-assigned IDs must be loaded before the user
    // edits prices (otherwise UpdatePricingElements re-inserts them as duplicates).
    useEffect(() => {
        if (!state.forceRefreshRPEs) return;
        setState(prev => ({ ...prev, forceRefreshRPEs: false }));
        refreshRPEs();
    }, [state.forceRefreshRPEs]);

    const addMissingPackagesRPEsToState = (rpes, currentState) => {
        // First, add missing Package RPEs for packages referenced in RatePricingElementsPackages
        // Only consider active, non-override RPEs to avoid re-adding deleted packages
        const referencedPackageIds = [...new Set(
            rpes.filter(rpe => rpe.IsActive && !rpe.IsOverride)
                .flatMap(rpe => rpe.RatePricingElementsPackages?.map(rpep => rpep.PackageId) || [])
        )];
        const existingPackageRPEIds = new Set(
            rpes.filter(rpe => rpe.EntityTypeId === EntityTypes.Package && rpe.IsActive).map(rpe => rpe.EntityId)
        );

        for (const packageId of referencedPackageIds) {
            if (!existingPackageRPEIds.has(packageId) && packages.some(p => p.Id === packageId)) {
                currentState = addRatePricingElementToState(currentState, EntityTypes.Package, packageId);
            }
        }

        // Then, add missing addon elements for existing packages
        const packageRPEs = currentState.RatePricingElements.filter(rpe => rpe.EntityTypeId === EntityTypes.Package && rpe.IsActive);
        
        for (const packageRPE of packageRPEs) {
            const currentPackage = packages.find(p => p.Id === packageRPE.EntityId);
            const packageAddonElements = currentPackage?.PackageElements.filter(pe => pe.EntityTypeId === EntityTypes.Addon) || [];

            const missingAddonElements = packageAddonElements.filter(ra => !currentState.RatePricingElements.some(rpe =>
                rpe.EntityTypeId === EntityTypes.Addon &&
                rpe.EntityId === ra.EntityId &&
                rpe.IsActive &&
                !rpe.IsOverride &&
                rpe.RatePricingElementsPackages?.some(rpep => rpep.PackageId === packageRPE.EntityId))
            );

            for (const missingAddonElement of missingAddonElements) {
                currentState = addRatePricingElementToState(
                    currentState,
                    EntityTypes.Addon,
                    missingAddonElement.EntityId,
                    packageRPE.EntityId
                );
            }
        }
        return currentState;
    }

    const getSelectedPackages = () => {
        var s = [...new Set(state.RatePricingElements
            ?.flatMap(rpe => rpe.RatePricingElementsPackages
                ?.map(rpep => rpep.PackageId)))];
        return s;
    }

    const sortedGridData = useCallback(() => {
        const sorted = gridData.sort((a, b) => b.Type.localeCompare(a.Type) ||
            b.IsPackage - a.IsPackage ||
            a.RateElementName.localeCompare(b.RateElementName)
        )
        return sorted
    }, [gridData])



    const isConsumptionAfterReservationDayDefault =
        useMemo(() =>
            getElement(
                selectedEntity.EntityTypeId,
                selectedEntity.EntityId)?.IsConsumptionAfterReservationDay,
            [selectedEntity]);

    const getAddonOccurrencesInNotOverridenPackages = (rpe) => {
        if (rpe && !rpe.IsPackageElement) {
            return 0;
        }

        let packageIds = state.RatePricingElements?.filter(srpe => srpe.EntityTypeId === EntityTypes.Addon &&
            srpe.RatePricingElementsPackages?.length &&
            srpe.EntityId === rpe.EntityId)
            ?.flatMap(srpe => srpe.RatePricingElementsPackages.map(rpep => rpep.PackageId));

        if (!packageIds) return 0;

        packageIds = [...new Set(packageIds)];

        let overridePackageRPE = state.RatePricingElements.filter(rpe =>
            rpe.RateId == state.Id &&
            rpe.EntityTypeId == EntityTypes.Package &&
            packageIds.includes(rpe.EntityId) && rpe.IsOverride).map(rpe => rpe.EntityId);

        return packageIds.length - packageIds.filter(id => overridePackageRPE.includes(id)).length
    }

    const derivedRateElementName =
        useMemo(() =>
            getElementName(
                selectedEntity.EntityTypeId,
                selectedEntity.EntityId,
                getAddonOccurrencesInNotOverridenPackages(selectedEntity)),
            [selectedEntity]);

    const restoreDerivedRPEs = async (RPEsRepresentingDeletionOfDerived) => {
        try {
            loader(true);
            await PriceLists.DeletePricingElements(RPEsRepresentingDeletionOfDerived);

            let RPEIdsRepresentingDeletionOfDerived = RPEsRepresentingDeletionOfDerived.map(rpe => rpe.Id);
            setState({ ...state, RatePricingElements: state.RatePricingElements?.filter(rpe => !RPEIdsRepresentingDeletionOfDerived.includes(rpe.Id)) });

            onSuccess();
        } catch (e) {
            onError(e);
        } finally {
            loader(false);
        }
    }

    const addonIsOverridenInAllPackages = (rpe) => {
        if (!rpe.RatePricingElementsPackages?.length) return false;

        let packageIds = rpe.RatePricingElementsPackages.map(rpep => rpep.PackageId);

        let overridePackageRPE = state.RatePricingElements.filter(rpe =>
            rpe.RateId == state.Id &&
            rpe.EntityTypeId == EntityTypes.Package &&
            packageIds.includes(rpe.EntityId) && rpe.IsOverride).map(rpe => rpe.EntityId);

        return overridePackageRPE.length && packageIds.every(id => overridePackageRPE.includes(id))
    }

    const roomTypeAddonIndicator = useCallback(item => {
        return item.RoomTypeAddons?.length ? <Label circular color="green" empty />
            : undefined;
    }, []);

    const onPricingSave = ({ rpe, rpes, setPricings, setIsDirty, name }) => {
        const newPricings = RPEsEditRearangeDateranges(rpe, rpes, state.BaseRateId);
        if (name) {
            setRPEsWithRearangedExceptions(newPricings, name);
        }
        setPricings(newPricings.map(rpe => ({ ...rpe, isEdit: false })));
        setIsDirty(true);

    }

    return (
        <Grid columns={3} style={{ flex: 1, overflow: "hidden" }}>
            <Grid.Row>

                <HideControlsByRateType rates={[RateCategory.ExtraAddOn]}>
                    <Grid.Column>
                        <Panel
                            collapsable
                            collapsed={collapsed}
                            onCollapse={setCollapsed}
                            header={text("RoomTypes")}
                            info={"RoomTypesInfo"}>
                            <Form className="selection-top">
                                <Form.Field style={{ display: 'flex' }}>
                                    <RoomTypesDropDown
                                        onChange={handleRoomTypeSelected}
                                        data={roomTypeDropdownOptions}
                                        value={selectedRoomTypes}
                                        search
                                        multiple
                                        clearable
                                    />
                                    <Button disabled={roomTypeDropdownOptions.length === 0} onClick={addRoomType}>{text("Add")}</Button>
                                </Form.Field>
                            </Form>
                            <Form className="selection-list">
                                <List divided verticalAlign='middle' >
                                    {
                                        groupByIntoArray(state.RatePricingElements?.filter(rpe =>
                                            rpe.EntityTypeId === EntityTypes.RoomType && rpe.IsActive && !rpe.IsOverride), "EntityId")
                                            .map(group => {
                                                let shortNameString = roomTypes.find(rt => rt.Id === group[0].EntityId)?.ShortName;
                                                let deletedDerivedRPEs = group.filter((rpe) => state.RatePricingElements.find(x => rpe.EntityTypeId === x.EntityTypeId && rpe.EntityId === x.EntityId && x.IsOverride));
                                                let RPEsRepresentingDeletionOfDerived = state.RatePricingElements.filter(rpe => rpe.EntityTypeId === EntityTypes.RoomType && rpe.EntityId === group[0].EntityId && rpe.IsOverride);
                                                let activeRPEs = group.filter((rpe) => !rpe.IsOverride && !deletedDerivedRPEs.map(x => x.Id).includes(rpe.Id));
                                                let deletionDisabled = group[0].IsSupplement || activeRPEs.length === 0;
                                                return (
                                                    <List.Item key={group[0].EntityId} disabled={group[0].IsSupplement}>
                                                        {roomTypes.find(rt => rt.Id === group[0].EntityId)?.Name} {shortNameString ? `(${shortNameString})` : ""}
                                                        <Icon link onClick={() => { deleteEntity(group[0]) }} disabled={deletionDisabled} name={'delete'} className="delete-button" size='small' />
                                                        {deletedDerivedRPEs.length > 0 && <Icon onClick={() => restoreDerivedRPEs(RPEsRepresentingDeletionOfDerived)} name="redo" style={{ float: "right", marginRight: 5 }} size="small" />}
                                                    </List.Item>
                                                )
                                            })
                                    }
                                </List>
                            </Form>
                        </Panel>
                    </Grid.Column>
                    <Grid.Column>
                        <Panel
                            collapsable
                            collapsed={collapsed}
                            onCollapse={setCollapsed}
                            header={text("Packages")}
                            info={"PackagesInfo"}>
                            <Form className="selection-top">
                                <Form.Field style={{ display: 'flex' }}>
                                    <PackagesDropDown
                                        onChange={handlePackageSelected}
                                        data={packageDropdownOptions}
                                        value={selectedPackages}
                                        search
                                        multiple
                                        clearable
                                    />
                                    <Button disabled={packageDropdownOptions.length == 0} onClick={addPackage}>{text("Add")}</Button>
                                </Form.Field>
                            </Form>
                            <Form className="selection-list">
                                <List divided verticalAlign='middle'>
                                    {
                                        groupByIntoArray(
                                            state.RatePricingElements?.filter(rpe =>
                                                rpe.EntityTypeId === EntityTypes.Package &&
                                                rpe.IsActive && !rpe.IsOverride &&
                                                getSelectedPackages().includes(rpe.EntityId)), "EntityId").map(group => {
                                                    let deletedDerivedRPEs = group.filter((rpe) => state.RatePricingElements.find(x => rpe.EntityTypeId === x.EntityTypeId &&
                                                        rpe.EntityId === x.EntityId && x.IsOverride));
                                                    let RPEsRepresentingDeletionOfDerived = state.RatePricingElements.filter(rpe => rpe.EntityTypeId === EntityTypes.Package &&
                                                        rpe.EntityId === group[0].EntityId && rpe.IsOverride);
                                                    let activeRPEs = group.filter((rpe) => !rpe.IsOverride && !deletedDerivedRPEs.map(x => x.Id).includes(rpe.Id));
                                                    let deletionDisabled = activeRPEs.length === 0;
                                                    return (
                                                        <List.Item key={group[0].EntityId}>
                                                            {packages.find(rt => rt.Id === group[0].EntityId)?.Name}
                                                            <Icon
                                                                link
                                                                onClick={() => { deleteEntity(group[0]) }}
                                                                name="delete"
                                                                disabled={deletionDisabled}
                                                                className="delete-button"
                                                                size='small' />
                                                            {deletedDerivedRPEs.length > 0 &&
                                                                <Icon onClick={() => restoreDerivedRPEs(RPEsRepresentingDeletionOfDerived)}
                                                                    name="redo" style={{ float: "right", marginRight: 5 }} size="small" />}
                                                        </List.Item>
                                                    )
                                                })
                                    }
                                </List>
                            </Form>
                        </Panel>
                    </Grid.Column>
                </HideControlsByRateType>
                <Grid.Column>
                    <Panel
                        collapsable
                        collapsed={collapsed}
                        onCollapse={setCollapsed}
                        header={text("Addons")}
                        info={"AddonsInfo"}>
                        <Form className="selection-top">
                            <Form.Field style={{ display: 'flex' }}>
                                <ChainAddonsDropDown
                                    onChange={handleAddonSelected}
                                    data={addonDropdownOptions}
                                    value={selectedAddons}
                                    search
                                    multiple
                                    clearable
                                    labelField={roomTypeAddonIndicator}
                                />
                                <Button disabled={addonDropdownOptions.length === 0} onClick={addAddon}>{text("Add")}</Button>
                            </Form.Field>
                        </Form>
                        <Form className="selection-list">
                            <List divided verticalAlign='middle'>
                                {
                                    groupByIntoArray(state.RatePricingElements?.filter(rpe =>
                                        rpe.EntityTypeId === EntityTypes.Addon &&
                                        rpe.IsActive && !rpe.IsOverride &&
                                        !addonIsOverridenInAllPackages(rpe))
                                        .map(rpe => ({ ...rpe, IsPackageElement: Boolean(rpe.RatePricingElementsPackages?.length || 0) })), "EntityId", "IsPackageElement")
                                        .map(group => {
                                            let deletedDerivedRPEs = group.filter((rpe) => state.RatePricingElements.find(
                                                x => rpe.EntityTypeId === x.EntityTypeId &&
                                                    rpe.EntityId === x.EntityId &&
                                                    (rpe.IsPackageElement || 0) === (x.RatePricingElementsPackages?.length || 0) &&
                                                    x.IsOverride === true));
                                            let RPEsRepresentingDeletionOfDerived = state.RatePricingElements.filter(
                                                rpe => rpe.EntityTypeId === EntityTypes.Addon &&
                                                    rpe.EntityId === group[0].EntityId &&
                                                    (rpe.RatePricingElementsPackages?.length || 0) === (group[0].IsPackageElement || 0) &&
                                                    rpe.IsOverride);
                                            let activeRPEs = group.filter((rpe) => !rpe.IsOverride && !deletedDerivedRPEs.map(x => x.Id).includes(rpe.Id));
                                            let isDisabled = group[0].IsSupplement || group[0].IsPackageElement || activeRPEs.length === 0
                                            return (
                                                <List.Item key={`${group[0].EntityId}_${group[0].IsPackageElement}`}>
                                                    {addons.find(rt => rt.Id === group[0].EntityId)?.Name} {group[0].IsPackageElement ? "(Package)" : ""}
                                                    <Icon link onClick={() => { deleteEntity(group[0]) }} name={'delete'} disabled={isDisabled} className="delete-button" size='small' />
                                                    {deletedDerivedRPEs.length > 0 && <Icon onClick={() => restoreDerivedRPEs(RPEsRepresentingDeletionOfDerived)} name="redo" style={{ float: "right", marginRight: 5 }} size="small" />}
                                                </List.Item>
                                            )
                                        })
                                }
                            </List>
                        </Form>
                    </Panel>
                </Grid.Column>

            </Grid.Row>
            <Grid.Row className="price-element-list">
                <div className="price-element-list-container">
                    <GridPanel options={gridOptions} data={sortedGridData()}></GridPanel>
                </div>

                <PricingPerPersonPopup
                    onDelete={deleteRPEFromPopups}
                    open={showPricingPerPersonPopup}
                    onClose={() => setShowPricingPerPersonPopup(false)}
                    EntityId={selectedEntity?.EntityId}
                    EntityTypeId={selectedEntity?.EntityTypeId}
                    Packages={selectedEntity.RatePricingElementsPackages}
                    updateRPEs={args => updateRPEs({ ...args, onClose: () => setShowPricingPerPersonPopup(false) })}
                    onPricingSave={onPricingSave}
                    rateElementName={derivedRateElementName}
                />
                <PricingPerRoomPopup
                    onDelete={deleteRPEFromPopups}
                    open={showPricingPerRoomPopup}
                    onClose={() => setShowPricingPerRoomPopup(false)}
                    EntityId={selectedEntity?.EntityId}
                    EntityTypeId={selectedEntity?.EntityTypeId}
                    Packages={selectedEntity.RatePricingElementsPackages}
                    updateRPEs={args => updateRPEs({ ...args, onClose: () => setShowPricingPerRoomPopup(false) })}
                    onPricingSave={onPricingSave}
                    rateElementName={derivedRateElementName}
                />
                <PricingPerVillaPopup
                    onDelete={deleteRPEFromPopups}
                    open={showPricingPerPVillaPopup}
                    onClose={() => setShowPricingPerVillaPopup(false)}
                    EntityId={selectedEntity?.EntityId}
                    EntityTypeId={selectedEntity?.EntityTypeId}
                    Packages={selectedEntity.RatePricingElementsPackages}
                    updateRPEs={args => updateRPEs({ ...args, onClose: () => setShowPricingPerVillaPopup(false) })}
                    onPricingSave={onPricingSave}
                    rateElementName={derivedRateElementName}
                />
                <AddonOptionsPopup
                    onDelete={deleteRPEFromPopups}
                    open={showAddonOptionsPopup}
                    onClose={() => setShowAddonOptionsPopup(false)}
                    hotelAddon={selectedEntity?.HotelAddons?.[0]}
                    onUpdate={changeAddonOptions}
                    updateRPEs={refreshRPEs}
                    isConsumptionAfterReservationDayDefault={isConsumptionAfterReservationDayDefault}
                    rateElementName={derivedRateElementName}
                    isDerived={isDerived && selectedEntity?.HotelAddons?.[0] && selectedEntity?.HotelAddons?.[0].RateId !== state.Id}
                />
                <PerPersonAddonPopup
                    onDelete={deleteRPEFromPopups}
                    open={showPersonAddonPopup}
                    onClose={() => setShowPersonAddonPopup(false)}
                    EntityId={selectedEntity?.EntityId}
                    EntityTypeId={selectedEntity?.EntityTypeId}
                    Packages={selectedEntity.RatePricingElementsPackages}
                    updateRPEs={args => updateRPEs({ ...args, onClose: () => setShowPersonAddonPopup(false) })}
                    onPricingSave={onPricingSave}
                    RatePricingTypeId={selectedEntity.RatePricingTypeId}
                    rateElementName={derivedRateElementName}
                />
                <PerRoomAddonPopup
                    onDelete={deleteRPEFromPopups}
                    open={showRoomAddonPopup}
                    onClose={() => setShowRoomAddonPopup(false)}
                    EntityId={selectedEntity?.EntityId}
                    EntityTypeId={selectedEntity?.EntityTypeId}
                    Packages={selectedEntity.RatePricingElementsPackages}
                    updateRPEs={args => updateRPEs({ ...args, onClose: () => setShowRoomAddonPopup(false) })}
                    onPricingSave={onPricingSave}
                    RatePricingTypeId={selectedEntity.RatePricingTypeId}
                    rateElementName={derivedRateElementName}
                />
                <CombinationsPopup
                    onDelete={deleteRPEFromPopups}
                    open={showCombinationsPopup}
                    onClose={() => setShowCombinationsPopup(false)}
                    EntityId={selectedEntity?.EntityId}
                    EntityTypeId={selectedEntity?.EntityTypeId}
                    Packages={selectedEntity.RatePricingElementsPackages}
                    pricingTemplates={pricingTemplates}
                    roomType={roomTypes?.find(x => x.Id === selectedEntity?.EntityId)}
                    updateRPEs={refreshRPEs}
                    rateElementName={derivedRateElementName}
                />
                <SupplementPopup
                    onDelete={deleteRPEFromPopups}
                    open={showSupplementPopup}
                    onClose={() => setShowSupplementPopup(false)}
                    EntityId={selectedEntity?.EntityId}
                    EntityTypeId={selectedEntity?.EntityTypeId}
                    Packages={selectedEntity.RatePricingElementsPackages}
                    roomTypes={roomTypes.filter(x => x.DisplayOrder !== 0 && state.RatePricingElements?.find(rpe => rpe.EntityTypeId === EntityTypes.RoomType && rpe.EntityId === x.Id))}
                    standardRoomTypeId={roomTypes.find(x => x.DisplayOrder === 0)?.Id}
                    updateRPEs={refreshRPEs}
                    isDerived={isDerived}
                    rateElementName={derivedRateElementName}
                />
            </Grid.Row>
        </Grid>
    );
};
